/**
 * Birch AI selection — Slice 6.
 *
 * `window.BirchAI.selection` is the cross-surface API. Any storefront page
 * can add a product to the selection (collection card checkbox, wishlist
 * "select" button, PDP "compare with similar", chat in-card "+" toggle,
 * cart "ask AI about these"); the chat widget then sends the selection on
 * the next request body. The orchestrator injects a USER SELECTION block
 * so reference resolution for "these"/"those" is deterministic.
 *
 * Storage: localStorage under `birch_ai_selection_v1`. Persists 30 minutes
 * (matches the selection-persistence decision in plan §3 open question 2).
 * Hard cap of 4 entries (matches `compare_products` and the prompt-builder
 * cap). FIFO eviction when adding a 5th.
 *
 * Events:
 *   - `birch:selection:changed` — fires after every add/remove/clear.
 *     Listeners (e.g. floating action bar) read window.BirchAI.selection.list().
 *
 * Helpers:
 *   - `BirchAI.selection.list()` — current entries
 *   - `BirchAI.selection.add(entry)` — idempotent on (kind+handle)
 *   - `BirchAI.selection.remove(handle)` — drops by handle
 *   - `BirchAI.selection.has(handle)` — boolean
 *   - `BirchAI.selection.clear()` — empty
 *   - `BirchAI.openWithContext({ query?, source, query?, fresh? })` —
 *     dispatches `birch:open-with-context` with the current selection.
 */

// @ts-check
// Wrapped in an IIFE so its top-level constants (STORAGE_KEY etc.) are
// scoped inside the function. Without this, loading as a classic script
// collides with cart-restore.js, which also declares a top-level
// `const STORAGE_KEY` — the second `const` then throws a SyntaxError
// that takes down every classic script after it.
(function () {
  'use strict';

const STORAGE_KEY = 'birch_ai_selection_v1';
const MAX_ENTRIES = 10;
const TTL_MS = 30 * 60 * 1000; // 30 minutes

/** @typedef {{ source: 'collection'|'wishlist'|'pdp'|'chat'|'cart'|'history', kind: 'birch'|'lushlife', handle: string, title?: string, variantId?: string }} SelectionEntry */

/** @returns {{ entries: SelectionEntry[], ts: number }} */
function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { entries: [], ts: 0 };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { entries: [], ts: 0 };
    if (Date.now() - (parsed.ts ?? 0) > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return { entries: [], ts: 0 };
    }
    const entries = Array.isArray(parsed.entries) ? parsed.entries.slice(0, MAX_ENTRIES) : [];
    return { entries, ts: parsed.ts ?? 0 };
  } catch { return { entries: [], ts: 0 }; }
}

/** @param {SelectionEntry[]} entries */
function writeStorage(entries) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ entries, ts: Date.now() })); }
  catch { /* private mode, full storage — ignore */ }
}

function dispatchChange() {
  window.dispatchEvent(new CustomEvent('birch:selection:changed', { detail: { entries: list() } }));
}

/** @returns {SelectionEntry[]} */
function list() { return readStorage().entries; }

/** @param {string} handle */
function has(handle) { return list().some((e) => e.handle === handle); }

/**
 * @param {SelectionEntry} entry
 * @returns {boolean} true if added (or already present), false if rejected at the cap
 */
function add(entry) {
  if (!entry || !entry.handle || !entry.kind) return false;
  const existing = list();
  if (existing.some((e) => e.handle === entry.handle && e.kind === entry.kind)) return true; // idempotent
  // At capacity: refuse the new pick rather than silently evicting the first
  // one (FIFO eviction surprised users — the earliest selection vanished).
  // Signal a limit event so the UI can tell the user to deselect first.
  if (existing.length >= MAX_ENTRIES) {
    window.dispatchEvent(new CustomEvent('birch:selection:limit', { detail: { max: MAX_ENTRIES } }));
    return false;
  }
  const next = [...existing, entry];
  writeStorage(next);
  dispatchChange();
  return true;
}

/** @param {string} handle */
function remove(handle) {
  const next = list().filter((e) => e.handle !== handle);
  writeStorage(next);
  dispatchChange();
}

function clear() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  dispatchChange();
}

/**
 * Open the chat with the current selection (or override) attached.
 * @param {{ query?: string, source?: string, fresh?: boolean }} [opts]
 */
function openWithContext(opts) {
  const o = opts || {};
  const detail = {
    selection: list(),
    ...(o.query !== undefined && { query: o.query }),
    ...(o.source !== undefined && { source: o.source }),
    // Default per refined §3: continue (auto). Caller passes `fresh: true`
    // to override (nav search box, explicit reset, idle).
    fresh: o.fresh === true,
  };
  window.dispatchEvent(new CustomEvent('birch:open-with-context', { detail }));
}

/** @type {any} */ (window).BirchAI = /** @type {any} */ (window).BirchAI || {};
/** @type {any} */ (window).BirchAI.selection = { list, add, remove, has, clear, max: MAX_ENTRIES };
/** @type {any} */ (window).BirchAI.openWithContext = openWithContext;

})();
